import React, { useState } from 'react'
import {
  SYNTHETIC_ATHLETES,
  SyntheticAthlete,
  getIndividualPlayerPerformance,
} from './syntheticDashboardData'
import {
  Zap,
  Target,
  Shield,
  Layers,
  ArrowRight,
  Share2,
  Lock,
} from 'lucide-react'

interface IndividualPlayerPerformanceViewProps {
  onOpenDrawer?: (athlete: SyntheticAthlete) => void
  isAthleteView?: boolean
  initialAthleteId?: string
}

export const SyntheticIndividualPlayerPerformanceView: React.FC<IndividualPlayerPerformanceViewProps> = ({
  onOpenDrawer,
  isAthleteView = false,
  initialAthleteId = 'ath-01',
}) => {
  // En vista jugador, el atleta está restringido a su propia identidad
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>(
    isAthleteView ? 'ath-01' : initialAthleteId
  )

  const athletes = SYNTHETIC_ATHLETES
  const currentAthlete = athletes.find((a) => a.id === selectedAthleteId) || athletes[0]
  const data = getIndividualPlayerPerformance(currentAthlete.id)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header: Athlete Identity, Selector & Privacy Context */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '10px',
            background: 'var(--brand-red, #b91c1c)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.45rem',
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            boxShadow: '0 2px 6px rgba(185, 28, 28, 0.3)',
          }}>
            #{currentAthlete.dorsal}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.3rem',
                fontWeight: 800,
                color: 'var(--text-main, #0f172a)',
                letterSpacing: '-0.02em',
              }}>
                {currentAthlete.name}
              </h2>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '4px',
                background: currentAthlete.unit === 'Ataque' ? 'rgba(217, 119, 6, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                color: currentAthlete.unit === 'Ataque' ? '#b45309' : '#1d4ed8',
              }}>
                {currentAthlete.position} · {currentAthlete.unit}
              </span>
            </div>

            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #475569)', marginTop: '2px' }}>
              {currentAthlete.club} · {data.totalSnapsPlayed} snaps registrados en el ciclo 2026
            </div>
          </div>
        </div>

        {/* Player Selector (Disabled/Hidden in athlete view for strict privacy) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isAthleteView ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '6px',
              background: 'var(--surface-subtle, #f8fafc)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              fontSize: '0.75rem',
              color: 'var(--text-muted, #64748b)',
            }}>
              <Lock size={12} />
              <span>Vista propia del atleta · Acceso a estadísticas personales</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="athlete-select" style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
                Cambiar atleta:
              </label>
              <select
                id="athlete-select"
                value={selectedAthleteId}
                onChange={(e) => setSelectedAthleteId(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--surface-border, #cbd5e1)',
                  background: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: 'var(--text-main, #0f172a)',
                  cursor: 'pointer',
                }}
              >
                {athletes.map((a) => (
                  <option key={a.id} value={a.id}>
                    #{a.dorsal} {a.name} ({a.position} · {a.unit})
                  </option>
                ))}
              </select>
            </div>
          )}

          {onOpenDrawer && (
            <button
              type="button"
              onClick={() => onOpenDrawer(currentAthlete)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '6px',
                background: 'var(--brand-red, #b91c1c)',
                color: '#ffffff',
                border: 'none',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <span>Ver Ficha Completa</span>
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Real Function KPIs: Passing, Receiving, Defense */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Passing Function Block (Visible if the player threw passes, regardless of nominal position!) */}
        {data.passing && (
          <div style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={16} color="#d97706" />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                  Función de Pase en Campo
                </h3>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
                {currentAthlete.position !== 'QB' ? 'Acciones de pase en formación táctica (Hydra/Reversible)' : 'Pasador principal'}
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '0.75rem',
            }}>
              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>COMPLETOS / INT</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {data.passing.completions} / {data.passing.attempts}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#15803d', fontWeight: 700 }}>{data.passing.completionPct}% efectividad</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>YARDAS DE PASE</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {data.passing.yards} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>yd</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.passing.yardsPerAttempt} yd / intento</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>TOUCHDOWNS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--brand-red, #b91c1c)' }}>
                  {data.passing.touchdowns} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>TD</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.passing.explosivePasses} pases explosivos</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>INTERCEPCIONES</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#d97706' }}>
                  {data.passing.interceptions} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>INT</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.passing.sacksSuffered} sacks sufridos</div>
              </div>
            </div>
          </div>
        )}

        {/* Receiving Function Block */}
        {data.receiving && (
          <div style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Target size={16} color="#0284c7" />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                  Función de Recepción y Rutas
                </h3>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
                {data.receiving.targetsKnown} targets anotados en partidos
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '0.75rem',
            }}>
              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>RECEPCIONES / TARGETS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {data.receiving.receptions} / {data.receiving.targetsKnown}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#15803d', fontWeight: 700 }}>{data.receiving.catchPct}% de captura</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>YARDAS DE RECEPCIÓN</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {data.receiving.yards} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>yd</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.receiving.yardsPerTarget} yd / target</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>TOUCHDOWNS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--brand-red, #b91c1c)' }}>
                  {data.receiving.touchdowns} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>TD</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Ganancia máx: {data.receiving.longGain} yd</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>DROPS REGISTRADOS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#64748b' }}>
                  {data.receiving.drops}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Pases no atrapados</div>
              </div>
            </div>
          </div>
        )}

        {/* Defense Function Block */}
        {data.defense && (
          <div style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={16} color="#15803d" />
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                  Función Defensiva en Campo
                </h3>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
                {data.defense.flagAttempts} intentos de flag disputados
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '0.75rem',
            }}>
              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>FLAGS CONSEGUIDOS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  {data.defense.flagsMade} / {data.defense.flagAttempts}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#15803d', fontWeight: 700 }}>{data.defense.flagTacklePct}% efectividad</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>INTERCEPCIONES</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--brand-red, #b91c1c)' }}>
                  {data.defense.interceptions} <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>INT</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.defense.passesDefended} pases defendidos</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>PRESIONES AL QB</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#d97706' }}>
                  {data.defense.pressures}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{data.defense.sacks} sacks confirmados</div>
              </div>

              <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>FLAGS FALLADOS</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#64748b' }}>
                  {data.defense.flagsMissed}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Tackles eludidos</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Longitudinal Comparison: Period A (Marzo) vs Period B (Mayo) */}
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Evolución Comparativa: Concentración Marzo vs Mayo 2026
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Contraste formal entre periodos equivalentes de preparación (sin mezclar contextos dispares)
            </p>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '6px' }}>
              {data.periodComparison.periodA.label} ({data.periodComparison.periodA.snaps} snaps)
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{data.periodComparison.periodA.metric1}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>{data.periodComparison.periodA.val1}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{data.periodComparison.periodA.metric2}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>{data.periodComparison.periodA.val2}</div>
              </div>
            </div>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid var(--brand-red, #b91c1c)', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--brand-red, #b91c1c)', marginBottom: '6px' }}>
              {data.periodComparison.periodB.label} ({data.periodComparison.periodB.snaps} snaps) · RECIENTE
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{data.periodComparison.periodB.metric1}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#15803d' }}>
                  {data.periodComparison.periodB.val1}
                  <span style={{ fontSize: '0.72rem', color: '#15803d', marginLeft: '4px' }}>
                    (+{(data.periodComparison.periodB.val1 - data.periodComparison.periodA.val1).toFixed(1)})
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{data.periodComparison.periodB.metric2}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#15803d' }}>
                  {data.periodComparison.periodB.val2}
                  <span style={{ fontSize: '0.72rem', color: '#15803d', marginLeft: '4px' }}>
                    (+{(data.periodComparison.periodB.val2 - data.periodComparison.periodA.val2).toFixed(1)})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connections & Teammate Associations (BK-05 / BK-06 Subalcance M8C) */}
      {!isAthleteView && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '1.25rem',
        }}>
          {/* Conexiones Lanzador — Receptor */}
          <div style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
              <Share2 size={16} color="var(--brand-red, #b91c1c)" />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Conexiones Lanzador — Receptor
              </h3>
            </div>
            <p style={{ margin: '0 0 0.85rem 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Química y volumen de pases con compañeros. Muestra oportunidades reales sin rankings arbitrarios de «mejor conexión».
            </p>

            {data.connections.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.connections.map((c) => (
                  <div
                    key={c.targetAthleteId}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                        #{c.targetDorsal} {c.targetName}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        {c.sampleNote} · {c.touchdowns} TD anotados
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#15803d' }}>
                        {c.completions}/{c.attempts} ({c.completionPct}%)
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        {c.yards} yardas conseguidas
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                Sin registros de conexiones directas para este perfil de jugador.
              </div>
            )}
          </div>

          {/* Asociaciones al compartir campo (Con/Sin coincidencia) */}
          <div style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
              <Layers size={16} color="#0284c7" />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Rendimiento al Coincidir en Campo
              </h3>
            </div>
            <p style={{ margin: '0 0 0.85rem 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Comparativa del equipo con y sin el compañero en el quinteto. No presupone causalidad.
            </p>

            {data.withWithoutTeammates.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.withWithoutTeammates.map((w) => (
                  <div
                    key={w.teammateId}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                    }}
                  >
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
                      Con #{w.teammateDorsal} {w.teammateName}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.72rem' }}>
                      <div style={{ background: '#ffffff', padding: '4px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#15803d', fontWeight: 700 }}>JUNTOS: </span>
                        <strong>{w.yardsPerSnapTogether} yd/snap</strong> ({w.snapsTogether} snaps)
                      </div>
                      <div style={{ background: '#ffffff', padding: '4px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#64748b', fontWeight: 700 }}>SIN COINCIDIR: </span>
                        <strong>{w.yardsPerSnapWithout} yd/snap</strong> ({w.snapsWithout} snaps)
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                Sin datos suficientes de combinaciones de quinteto para este jugador.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
